import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { TableHead, TableRow, TableCell, Box, Checkbox } from '@mui/material';
import type { Locale, TableDescriptor, TableColumnDescriptor } from '@finance/contracts';
import type { TableState, TableSortState, TableFilterState } from './table-state.js';
import { getVisibleOrderedColumns, getPinnedColumnOffsets } from './table-state.js';
import { TableFilterRow } from './TableFilterRow.js';
import { resolveColumnSizing } from './column-sizing.js';
import { tableTokens } from './table-tokens.js';
import { TableSortIndicator, type SortDirection } from './TableSortIndicator.js';
import { useAppI18n } from '../../../i18n/use-app-i18n';

interface TableHeaderProps {
  descriptor: TableDescriptor;
  state: TableState;
  onSortChange: (sorting: TableSortState[]) => void;
  onColumnResize?: (columnId: string, width: number) => void;
  onColumnReorder?: (fromColumnId: string, toColumnId: string) => void;
  onColumnVisibilityChange?: (columnId: string, visible: boolean) => void;
  onColumnPin?: (columnId: string, side: 'left' | 'right') => void;
  onColumnUnpin?: (columnId: string) => void;
  pinnedLeft: string[];
  pinnedRight: string[];
  showFilterRow: boolean;
  onToggleFilterRow: () => void;
  onFilterSet: (filter: TableFilterState) => void;
  onFilterRemove: (field: string) => void;
  selectionEnabled: boolean;
  allPageSelected: boolean;
  indeterminate: boolean;
  onSelectAll: (checked: boolean) => void;
  hasRowActions: boolean;
  locale?: Locale;
}

interface ResizeSession {
  columnId: string;
  startX: number;
  initialWidth: number;
  minWidth: number;
  maxWidth: number;
}

export function TableHeader({
  descriptor,
  state,
  onSortChange,
  onColumnResize,
  onColumnReorder,
  pinnedLeft,
  pinnedRight,
  showFilterRow,
  onFilterSet,
  onFilterRemove,
  selectionEnabled,
  allPageSelected,
  indeterminate,
  onSelectAll,
  hasRowActions,
  locale,
}: TableHeaderProps) {
  const { text, runtimeText, language } = useAppI18n();
  const resolvedLocale: Locale = locale ?? (language.startsWith('ne') ? 'ne' : 'en');
  const visibleColumns = useMemo(
    () => getVisibleOrderedColumns(descriptor, state),
    [descriptor, state],
  );
  const columnOffsets = useMemo(
    () => getPinnedColumnOffsets(visibleColumns, pinnedLeft, pinnedRight, state.columnSizes),
    [visibleColumns, pinnedLeft, pinnedRight, state.columnSizes],
  );
  const sortingEnabled = descriptor.capabilities?.sorting !== false;
  const resizingEnabled = descriptor.capabilities?.columnResizing !== false;
  const reorderingEnabled = descriptor.capabilities?.columnReordering !== false;
  const MIN_COLUMN_WIDTH = 50;

  const [draggingColumnId, setDraggingColumnId] = useState<string | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);
  const [resizing, setResizing] = useState<ResizeSession | null>(null);

  const getColumnWidth = useCallback(
    (columnId: string) =>
      state.columnSizes.find(size => size.columnId === columnId)?.width ??
      visibleColumns.find(column => column.id === columnId)?.defaultWidth,
    [state.columnSizes, visibleColumns],
  );

  useEffect(() => {
    if (!resizing || !onColumnResize) return;

    const handlePointerMove = (event: PointerEvent) => {
      const delta = event.clientX - resizing.startX;
      const nextWidth = Math.max(
        resizing.minWidth,
        Math.min(resizing.maxWidth, resizing.initialWidth + delta),
      );
      onColumnResize(resizing.columnId, nextWidth);
    };
    const stopResize = () => setResizing(null);

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopResize);
    window.addEventListener('pointercancel', stopResize);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopResize);
      window.removeEventListener('pointercancel', stopResize);
    };
  }, [resizing, onColumnResize]);

  const resizeBounds = useCallback(
    (column: TableColumnDescriptor) => ({
      currentWidth: getColumnWidth(column.id) ?? column.defaultWidth ?? 150,
      minWidth: column.minWidth ?? MIN_COLUMN_WIDTH,
      maxWidth: column.maxWidth ?? Number.MAX_SAFE_INTEGER,
    }),
    [getColumnWidth],
  );

  const handleResizeStart = useCallback(
    (event: React.PointerEvent, column: TableColumnDescriptor) => {
      if (!resizingEnabled || !onColumnResize) return;
      event.preventDefault();
      event.stopPropagation();
      const { currentWidth, minWidth, maxWidth } = resizeBounds(column);
      setResizing({
        columnId: column.id,
        startX: event.clientX,
        initialWidth: currentWidth,
        minWidth,
        maxWidth,
      });
    },
    [onColumnResize, resizeBounds, resizingEnabled],
  );

  const handleResizeKeyDown = useCallback(
    (event: React.KeyboardEvent, column: TableColumnDescriptor) => {
      if (!resizingEnabled || !onColumnResize) return;
      const { currentWidth, minWidth, maxWidth } = resizeBounds(column);
      const increment = event.shiftKey ? 25 : 10;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        onColumnResize(column.id, Math.min(currentWidth + increment, maxWidth));
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onColumnResize(column.id, Math.max(currentWidth - increment, minWidth));
      } else if (event.key === 'Home') {
        event.preventDefault();
        onColumnResize(column.id, column.defaultWidth ?? 150);
      }
    },
    [onColumnResize, resizeBounds, resizingEnabled],
  );

  const handleDragStart = useCallback(
    (event: React.DragEvent, columnId: string) => {
      if (!reorderingEnabled) {
        event.preventDefault();
        return;
      }
      event.dataTransfer.setData('text/plain', columnId);
      event.dataTransfer.effectAllowed = 'move';
      setDraggingColumnId(columnId);
    },
    [reorderingEnabled],
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent, columnId: string) => {
      if (!reorderingEnabled) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (columnId !== draggingColumnId) setDragOverColumnId(columnId);
    },
    [draggingColumnId, reorderingEnabled],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent, targetColumnId: string) => {
      if (!reorderingEnabled) return;
      event.preventDefault();
      const draggedColumnId = event.dataTransfer.getData('text/plain');
      setDragOverColumnId(null);
      setDraggingColumnId(null);
      if (!draggedColumnId || draggedColumnId === targetColumnId || !onColumnReorder) return;
      onColumnReorder(draggedColumnId, targetColumnId);
    },
    [onColumnReorder, reorderingEnabled],
  );

  const handleSortClick = useCallback(
    (column: TableColumnDescriptor) => {
      if (!column.sortable || !sortingEnabled) return;
      const currentSort = state.sorting.find(sort => sort.field === column.field);
      const nextSorting: TableSortState[] =
        currentSort?.direction === 'desc'
          ? state.sorting.filter(sort => sort.field !== column.field)
          : currentSort
            ? state.sorting.map(sort =>
                sort.field === column.field ? { ...sort, direction: 'desc' as const } : sort,
              )
            : [{ field: column.field, direction: 'asc' as const }];
      onSortChange(nextSorting);
    },
    [onSortChange, sortingEnabled, state.sorting],
  );

  const getSortDirection = (field: string): SortDirection =>
    state.sorting.find(sort => sort.field === field)?.direction ?? null;
  const getAriaSort = (field: string) => {
    const direction = getSortDirection(field);
    return direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none';
  };

  return (
    <TableHead>
      <TableRow>
        {selectionEnabled && (
          <TableCell padding="checkbox" component="th" scope="col">
            <Checkbox
              indeterminate={indeterminate}
              checked={allPageSelected}
              onChange={event => onSelectAll(event.target.checked)}
              aria-label={runtimeText('common.selectAll')}
            />
          </TableCell>
        )}
        {visibleColumns.map((column, index) => {
          const isPinnedLeft = pinnedLeft.includes(column.id);
          const isPinnedRight = pinnedRight.includes(column.id);
          const offset = columnOffsets[index];
          const sizing = resolveColumnSizing(column, getColumnWidth(column.id));
          const canSort = Boolean(column.sortable && sortingEnabled);
          const canReorder = Boolean(reorderingEnabled && column.reorderable !== false);
          const canResize = Boolean(resizingEnabled && column.resizable !== false && onColumnResize);
          const focusable = canSort || canReorder;
          const { currentWidth, minWidth, maxWidth } = resizeBounds(column);
          const columnLabel = text(column.labelKey);
          const wraps = column.wrapping === 'wrap';

          const headerSx: React.CSSProperties = {
            cursor: canSort ? 'pointer' : canReorder ? 'grab' : 'default',
            userSelect: 'none',
            whiteSpace: wraps ? 'normal' : 'nowrap',
            position: 'sticky',
            zIndex:
              isPinnedLeft || isPinnedRight
                ? tableTokens.zIndex.pinnedHeader
                : tableTokens.zIndex.header,
            left: isPinnedLeft ? `${offset.left}px` : undefined,
            right: isPinnedRight ? `${offset.right}px` : undefined,
            backgroundColor:
              dragOverColumnId === column.id ? 'action.selected' : tableTokens.pinned.headerBg,
            opacity: draggingColumnId === column.id ? 0.5 : 1,
            borderBottom: '2px solid',
            borderColor: 'divider',
            ...(isPinnedLeft && { boxShadow: tableTokens.pinned.shadowRight }),
            ...(isPinnedRight && { boxShadow: tableTokens.pinned.shadowLeft }),
          };
          if (sizing.width !== undefined) headerSx.width = sizing.width;
          headerSx.minWidth = sizing.minWidth;
          headerSx.maxWidth = sizing.maxWidth;

          return (
            <TableCell
              key={column.id}
              component="th"
              scope="col"
              draggable={canReorder}
              onClick={() => handleSortClick(column)}
              onDragStart={event => handleDragStart(event, column.id)}
              onDragOver={event => handleDragOver(event, column.id)}
              onDrop={event => handleDrop(event, column.id)}
              onDragEnd={() => {
                setDraggingColumnId(null);
                setDragOverColumnId(null);
              }}
              onKeyDown={event => {
                if (canSort && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  handleSortClick(column);
                  return;
                }
                if (canReorder && event.altKey) {
                  const targetIndex =
                    event.key === 'ArrowLeft'
                      ? index - 1
                      : event.key === 'ArrowRight'
                        ? index + 1
                        : -1;
                  const target = visibleColumns[targetIndex];
                  if (target && onColumnReorder) {
                    event.preventDefault();
                    onColumnReorder(column.id, target.id);
                  }
                }
              }}
              tabIndex={focusable ? 0 : -1}
              sx={headerSx}
              aria-sort={getAriaSort(column.field)}
              aria-keyshortcuts={canReorder ? 'Alt+ArrowLeft Alt+ArrowRight' : undefined}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <Box
                  component="span"
                  title={wraps ? undefined : columnLabel}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: wraps ? 'clip' : 'ellipsis',
                    whiteSpace: wraps ? 'normal' : 'nowrap',
                    overflowWrap: wraps ? 'anywhere' : undefined,
                    wordBreak: wraps ? 'break-word' : undefined,
                    lineHeight: 1.25,
                    flexShrink: 1,
                    minWidth: 0,
                  }}
                >
                  {columnLabel}
                </Box>
                {canSort && <TableSortIndicator direction={getSortDirection(column.field)} />}
              </Box>
              {canResize && (
                <Box
                  role="separator"
                  aria-label={text('table.resizeColumn')}
                  aria-orientation="vertical"
                  aria-valuemin={minWidth}
                  aria-valuemax={maxWidth}
                  aria-valuenow={currentWidth}
                  aria-keyshortcuts="ArrowLeft ArrowRight Shift+ArrowLeft Shift+ArrowRight Home"
                  tabIndex={0}
                  onPointerDown={event => handleResizeStart(event, column)}
                  onKeyDown={event => handleResizeKeyDown(event, column)}
                  sx={{
                    position: 'absolute',
                    right: -12,
                    top: 0,
                    bottom: 0,
                    width: 24,
                    cursor: 'col-resize',
                    touchAction: 'none',
                    zIndex: 30,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      left: '11px',
                      top: 0,
                      bottom: 0,
                      width: '2px',
                      bgcolor: resizing?.columnId === column.id ? 'primary.main' : 'transparent',
                    },
                    '&:hover::after, &:focus-visible::after': {
                      bgcolor: 'primary.main',
                    },
                    '@media (pointer: coarse), (max-width: 600px)': {
                      right: -22,
                      width: 44,
                      '&::after': { left: '21px' },
                    },
                  }}
                />
              )}
            </TableCell>
          );
        })}
        {hasRowActions && (
          <TableCell
            component="th"
            scope="col"
            sx={{
              position: 'sticky',
              right: 0,
              zIndex: tableTokens.zIndex.pinnedHeader,
              backgroundColor: tableTokens.pinned.headerBg,
              boxShadow: tableTokens.pinned.shadowLeft,
              minWidth: tableTokens.actionColumn.minWidth,
              width: tableTokens.actionColumn.width,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {text('table.actions')}
          </TableCell>
        )}
      </TableRow>
      {showFilterRow && (
        <TableFilterRow
          descriptor={descriptor}
          state={state}
          onFilterSet={onFilterSet}
          onFilterRemove={onFilterRemove}
          pinnedLeft={pinnedLeft}
          pinnedRight={pinnedRight}
          selectionEnabled={selectionEnabled}
          hasRowActions={hasRowActions}
          locale={resolvedLocale}
        />
      )}
    </TableHead>
  );
}
